import { Accordion, Stack } from '@mantine/core'
import React, { useState } from 'react'
import styled from 'styled-components'
import Icon from 'components/Icon'
import Link from 'components/Link'
import { MarkdownRenderer } from 'components/MarkdownRenderer'
import { Text } from 'components/Typography'
import { SPACE, FONT_WEIGHT, FONT_SIZE, COLOR_INTENT, FONT_STACK } from 'Theme'
import { type PublicProductQuery } from 'types/graphql-generated'
import { useMedia } from 'useMedia'
import { formatDimensionsText } from '../../utils'
import { ProductCustom } from '../ProductCustom'
import { Proposition65WarningModal } from '../Proposition65WarningModal'
import { VariantAccordion } from './VariantAccordion'

const AccordionHeaderText = ({
  children,
  ...rest
}: React.ComponentProps<typeof Text>) => (
  <Text
    color={COLOR_INTENT.VARIANT_PICKER.GROUP_COLOR}
    fontSize={FONT_SIZE.PX_13}
    fontWeight={FONT_WEIGHT.MEDIUM}
    letterSpacing="0.1em"
    {...rest}
  >
    {children}
  </Text>
)

export interface VariantDetailAccordionProps {
  selectedVariant: PublicProductQuery['product']['variants'][0]
  brand?: PublicProductQuery['product']['brand']
  detailsRef: React.RefObject<HTMLDivElement>
}

const TextButton = styled(Text)`
  &:hover {
    cursor: pointer;
  }
`

export const VariantDetails: React.FC<
  React.PWC<VariantDetailAccordionProps>
> = ({ selectedVariant, brand, detailsRef }) => {
  const [value, setValue] = useState<string | null | string[]>([])
  const [isProposition65ModalOpen, setIsProposition65ModalOpen] =
    useState(false)
  const accordionDetails = selectedVariant.customContent?.accordionDetails
  const customDimensions = selectedVariant.customContent?.customDimensions
  const isProp65Eligible = selectedVariant.managedCustomFields?.prop65Eligible
  const proposition65 = selectedVariant.managedCustomFields?.proposition65
  const shipping = selectedVariant.customContent?.accordionShipping
  const care = selectedVariant.customContent?.accordionCare
  const { MOBILE: isMobile } = useMedia()

  return (
    <>
      {(accordionDetails || isProp65Eligible) && (
        <Stack gap={SPACE.PX_6} my={SPACE.PX_22} ref={detailsRef}>
          <AccordionHeaderText>ABOUT THIS PIECE</AccordionHeaderText>
          <MarkdownRenderer
            markdownSource={accordionDetails}
            fontSize={FONT_SIZE.PX_14}
            fontFamily={FONT_STACK.SANS}
          />
          {isProp65Eligible && (
            <TextButton
              color={COLOR_INTENT.PRODUCT_ACCORDION.PROPOSITION_65_BUTTON}
              onClick={() => setIsProposition65ModalOpen(true)}
              mt={SPACE.PX_20}
              fontSize={FONT_SIZE.PX_16}
            >
              WARNING For California Residents
            </TextButton>
          )}
        </Stack>
      )}
      {isProp65Eligible && (
        <Proposition65WarningModal
          isOpen={isProposition65ModalOpen}
          onModalClose={() => setIsProposition65ModalOpen(false)}
          chemical={proposition65}
        />
      )}
      <VariantAccordion
        value={value}
        onChange={setValue}
        showTopBorder
        multiple
      >
        <Accordion.Item value="dimensions">
          <Accordion.Control
            chevron={
              value.includes('dimensions') ? <Icon.Minus /> : <Icon.Plus />
            }
          >
            <AccordionHeaderText>DIMENSIONS</AccordionHeaderText>
          </Accordion.Control>
          <Accordion.Panel>
            <Stack>
              {formatDimensionsText(selectedVariant).map(
                ({ key, value: dimension }) => (
                  <Text
                    key={key}
                    fontWeight={FONT_WEIGHT.MEDIUM}
                    fontSize={FONT_SIZE.PX_14}
                  >
                    {`${key}: `}
                    <Text fontSize={FONT_SIZE.PX_14}>{dimension}</Text>
                  </Text>
                )
              )}
              {customDimensions && (
                <Text
                  fontWeight={FONT_WEIGHT.MEDIUM}
                  fontSize={FONT_SIZE.PX_14}
                >
                  Other:{' '}
                  <Text fontSize={FONT_SIZE.PX_14}>{customDimensions}</Text>
                </Text>
              )}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>

        {brand && (
          <Accordion.Item value="brand">
            <Accordion.Control
              chevron={value.includes('brand') ? <Icon.Minus /> : <Icon.Plus />}
            >
              <AccordionHeaderText>BRAND</AccordionHeaderText>
            </Accordion.Control>
            <Accordion.Panel>
              <Link
                mb={SPACE.PX_4}
                fontWeight={FONT_WEIGHT.MEDIUM}
                fontSize={FONT_SIZE.PX_14}
                // `/` is needed for ensuring the link is not used as relative path
                href={`/${brand?.cmsBrandPage.cmsPage.slug}`}
              >
                {brand?.id}
              </Link>
            </Accordion.Panel>
          </Accordion.Item>
        )}

        {shipping && (
          <Accordion.Item value="shipping">
            <Accordion.Control
              chevron={
                value.includes('shipping') ? <Icon.Minus /> : <Icon.Plus />
              }
            >
              <AccordionHeaderText>SHIPPING & RETURNS</AccordionHeaderText>
            </Accordion.Control>
            <Accordion.Panel>
              <MarkdownRenderer
                markdownSource={shipping}
                fontSize={FONT_SIZE.PX_14}
                fontFamily={FONT_STACK.SANS}
              />
            </Accordion.Panel>
          </Accordion.Item>
        )}

        {care && (
          <Accordion.Item value="care">
            <Accordion.Control
              chevron={value.includes('care') ? <Icon.Minus /> : <Icon.Plus />}
            >
              <AccordionHeaderText>CARE</AccordionHeaderText>
            </Accordion.Control>
            <Accordion.Panel>
              <MarkdownRenderer
                markdownSource={care}
                fontSize={FONT_SIZE.PX_14}
                fontFamily={FONT_STACK.SANS}
              />
            </Accordion.Panel>
          </Accordion.Item>
        )}
      </VariantAccordion>
      {!isMobile && (
        <ProductCustom customContent={selectedVariant.customContent} />
      )}
    </>
  )
}
